// Vùng Lịch Luyện / tài nguyên P0 (WORLD §6.2, ENEMIES §3, BALANCE §5.1). Quái thường chỉ idle.
import type { Element } from '../elements';
import type { Phase } from '../screens';

export interface EnemyTemplateDef {
  readonly id: string;
  readonly family: string;
  readonly assetTier: 'a' | 'b' | 'c' | 'd';
  /** Ảnh trong public/assets/quai (không đuôi). */
  readonly art: string;
}

export interface EnemyFamilyDef {
  readonly id: string;
  readonly element: Element;
  /** Nguyên liệu tier c/d (R2) và tier b (5% trong R2). */
  readonly materials: { readonly common: readonly string[]; readonly elite: readonly string[] };
  readonly phase: Phase;
}

export const ENEMY_FAMILIES: readonly EnemyFamilyDef[] = [
  {
    id: 'lang',
    element: 'metal',
    materials: { common: ['mat.wolfFang', 'mat.wolfPelt'], elite: ['mat.bloodFang'] },
    phase: 'P0',
  },
  {
    id: 'ho',
    element: 'fire',
    materials: { common: ['mat.tigerBone', 'mat.tigerPelt'], elite: ['mat.tigerClaw'] },
    phase: 'P0',
  },
];

export const ENEMY_TEMPLATES: readonly EnemyTemplateDef[] = [
  { id: 'lang_d_hoang_lang', family: 'lang', assetTier: 'd', art: 'lang_d_hoang_lang' },
  { id: 'lang_c_thiet_nha_lang', family: 'lang', assetTier: 'c', art: 'lang_c_thiet_nha_lang' },
  { id: 'ho_d_son_ho', family: 'ho', assetTier: 'd', art: 'ho_d_son_ho' },
];

export function familyDef(id: string): EnemyFamilyDef {
  const d = ENEMY_FAMILIES.find((x) => x.id === id);
  if (!d) throw new RangeError(`unknown family: ${id}`);
  return d;
}

export function templateDef(id: string): EnemyTemplateDef {
  const d = ENEMY_TEMPLATES.find((x) => x.id === id);
  if (!d) throw new RangeError(`unknown enemy template: ${id}`);
  return d;
}

/** Sản vật thu thập theo giờ ở Trữ Lượng đầy (id tiền tệ trong ví hoặc id vật phẩm xếp chồng). */
export interface GatherYield {
  readonly id: string;
  readonly perHour: number;
}

export interface TrialZoneDef {
  readonly id: string;
  /** Khoảng bậc L của quái trong vùng: quái theo L người chơi, kẹp trong khoảng (DECISIONS). */
  readonly lMin: number;
  readonly lMax: number;
  readonly monsters: readonly { readonly template: string; readonly weight: number }[];
  readonly gather: readonly GatherYield[];
  /** Hồi Trữ Lượng mỗi canh giờ (điểm %, WORLD §6.2). */
  readonly regenPerWatch: number;
  /** Ghi đè mật độ quái (con/phút); mặc định ZONE_DENSITY_BY_REALM. */
  readonly density?: number;
  /** Nơi bế quan cùng vùng: Lịch Luyện ở đây hút khí theo nồng độ nơi này. */
  readonly siteId: string;
  /** Tọa độ node trên Địa Đồ (px thiết kế 390×844). */
  readonly node: { readonly x: number; readonly y: number };
  readonly phase: Phase;
}

export const TRIAL_ZONES: readonly TrialZoneDef[] = [
  {
    id: 'res.thanhHuVein',
    lMin: 1,
    lMax: 9,
    monsters: [],
    gather: [
      { id: 'spiritStone', perHour: 60 },
      { id: 'mat.springWater', perHour: 3 },
      { id: 'spiritShard.wood', perHour: 2 },
    ],
    regenPerWatch: 8,
    siteId: 'site.lk.servantQuarters',
    node: { x: 150, y: 330 },
    phase: 'P0',
  },
  {
    id: 'res.langCoc',
    lMin: 1,
    lMax: 9,
    monsters: [
      { template: 'lang_d_hoang_lang', weight: 45 },
      { template: 'lang_c_thiet_nha_lang', weight: 25 },
      { template: 'ho_d_son_ho', weight: 30 },
    ],
    gather: [
      { id: 'mat.spiritWood', perHour: 3 },
      { id: 'mat.beastCore1', perHour: 0.8 },
      { id: 'refiningStone', perHour: 3 },
    ],
    regenPerWatch: 12,
    siteId: 'site.lk.wolfValley',
    node: { x: 250, y: 470 },
    phase: 'P0',
  },
];

export function zoneDef(id: string): TrialZoneDef {
  const d = TRIAL_ZONES.find((x) => x.id === id);
  if (!d) throw new RangeError(`unknown trial zone: ${id}`);
  return d;
}

export function isZoneId(v: unknown): v is string {
  return typeof v === 'string' && TRIAL_ZONES.some((z) => z.id === v);
}
